import { z } from "zod";
import { PropertyTypeSchema, RoomTypeSchema, SpaceSchema } from "./listings";
import { RoommatePreferenceSchema, TenantProfileSchema } from "./account";

/**
 * Changes a signed-in visitor can make to their own data.
 *
 * Every body here is `strict`: an unexpected field is an error, not something
 * quietly dropped. That is what stops a caller from sending `userId` or
 * `isAdmin` along with an ordinary change and having it ride through.
 */

export const OkSchema = z.object({ ok: z.literal(true) });

/* ── Favourites ── */
export const ToggleFavoriteBodySchema = z.strictObject({ propertyId: z.uuid() });
export const ToggleFavoriteReplySchema = z.object({ favorited: z.boolean() });

/* ── Notifications ── */
export const MarkReadReplySchema = z.object({ updated: z.number().int() });

/* ── Saved searches ── */

/**
 * The filters a saved search stores, already parsed. Prices are integer cents.
 * These are the same keys the website's PERSISTED_FILTER_KEYS lists; `q` and
 * `radiusKm` are deliberately absent because they are resolved at request time
 * and would not mean the same thing when replayed later.
 */
export const PersistedFiltersSchema = z.strictObject({
  city: z.string().trim().min(1).max(200).optional(),
  province: z.string().trim().min(1).max(200).optional(),
  cityId: z.uuid().optional(),
  schoolId: z.uuid().optional(),
  gender: z.enum(["MALE", "FEMALE"]).optional(),
  roomType: RoomTypeSchema.optional(),
  space: SpaceSchema.optional(),
  propertyType: PropertyTypeSchema.optional(),
  minPrice: z.number().int().min(0).optional(),
  maxPrice: z.number().int().min(0).optional(),
  minCapacity: z.number().int().min(1).optional(),
});
export type PersistedFilters = z.infer<typeof PersistedFiltersSchema>;

export const CreateSavedSearchBodySchema = z.strictObject({ filters: PersistedFiltersSchema });
export const CreateSavedSearchReplySchema = z.object({ id: z.uuid(), name: z.string() });

export const UpdateSavedSearchBodySchema = z
  .strictObject({ alertsEnabled: z.boolean().optional(), frequency: z.enum(["INSTANT", "DAILY", "WEEKLY"]).optional() })
  .refine((b) => b.alertsEnabled !== undefined || b.frequency !== undefined, "Nothing to change.");

/* ── Push subscriptions ── */
export const PushSubscriptionBodySchema = z.strictObject({
  endpoint: z.url().max(1000),
  p256dh: z.string().min(1).max(300),
  auth: z.string().min(1).max(300),
});
export const RemovePushSubscriptionBodySchema = z.strictObject({ endpoint: z.string().min(1).max(1000) });

/* ── Roommate answers ── */
export const RoommatePreferenceBodySchema = z.strictObject({
  shareForMatching: z.boolean(),
  sleepSchedule: z.enum(["EARLY_BIRD", "FLEXIBLE", "NIGHT_OWL"]).nullable(),
  cleanliness: z.enum(["TIDY", "AVERAGE", "RELAXED"]).nullable(),
  noise: z.enum(["QUIET", "MODERATE", "LIVELY"]).nullable(),
  smoking: z.enum(["NONSMOKER", "OUTDOOR_ONLY", "SMOKER"]).nullable(),
  guests: z.enum(["RARELY", "SOMETIMES", "OFTEN"]).nullable(),
  studyHabit: z.enum(["AT_HOME", "MIXED", "LIBRARY"]).nullable(),
});
export type RoommatePreferenceBody = z.infer<typeof RoommatePreferenceBodySchema>;
export const RoommatePreferenceSavedSchema = z.object({ preference: RoommatePreferenceSchema });

/* ── The renter profile ── */
export const TenantProfileBodySchema = z.strictObject({
  gender: z.enum(["MALE", "FEMALE", "NONBINARY", "PREFER_NOT"]).nullable(),
  occupation: z.enum(["STUDENT", "WORKING", "OTHER"]).nullable(),
  dateOfBirth: z.iso.datetime().nullable(),
  /** Monthly, in cents. */
  budgetMin: z.number().int().min(0).nullable(),
  budgetMax: z.number().int().min(0).nullable(),
  moveInDate: z.iso.datetime().nullable(),
  leaseLengthPref: z.string().max(60).nullable(),
  bio: z.string().max(2000).nullable(),
  /** A school that does not exist is stored as "none", not rejected. */
  schoolId: z.uuid().nullable(),
  isLooking: z.boolean(),
  lifestyle: z.strictObject({ smoking: z.boolean(), pets: z.boolean(), cleanliness: z.string().max(40).nullable() }),
});
export type TenantProfileBody = z.infer<typeof TenantProfileBodySchema>;
export const TenantProfileSavedSchema = z.object({ profile: TenantProfileSchema });
